/**
 * UK postcode adapter (M9-B) — ONS Census 2021 demographics over UK statistical
 * geographies (region / local authority district / MSOA / postcode area). Second
 * proof of the adapter abstraction: identical {@link OverlayAdapter} contract,
 * renders through the M9-A engine with ZERO source-specific engine/render code.
 *
 * Boundaries: ONS Open Geography Portal boundaries (served as GeoJSON at ETL
 * time), keyed by ONS code (e.g. `E92000001`). Values: Nomis / ONS bulk CSV
 * (`geography_code,value`). Fetch behind the {@link HttpClient} seam; normalize
 * pure. Per-country labels/units — no forced cross-country normalization.
 */

import type { Boundary, DemographicDatum, DemographicVariable, Tier } from "../types";
import { buildCatalog, normalizeFeatureCollection, type FeatureCollection, type Seed } from "./shared";
import type { HttpClient, OverlayAdapter } from "./types";

export const UK_POSTCODE_SOURCE = "uk_postcode" as const;
export const UK_POSTCODE_TIERS = ["region", "lad", "msoa", "postcode_area"] as const;

export const UK_POSTCODE_CITATION =
  "Office for National Statistics (ONS), Census 2021. Contains public sector information licensed under the Open Government Licence v3.0.";

export const ONS_API_BASE = "https://www.nomisweb.co.uk/api/v01";

const UK_SEEDS: Seed[] = [
  ["KS101_USUAL_RESIDENTS", "Usual resident population", "people"],
  ["POP_DENSITY", "Population density per hectare", "people/ha"],
  ["MEDIAN_AGE", "Median age", "years"],
  ["TOTAL_HOUSEHOLDS", "Number of households", "households"],
  ["OWNED_OUTRIGHT", "Households that own their home outright", "households"],
  ["SOCIAL_RENTED", "Households in social rented accommodation", "households"],
  ["ECON_ACTIVE", "Economically active residents (16+)", "people"],
  ["DEGREE_QUALIFIED", "Residents with Level 4 qualifications or above", "people"],
];

/** The static UK/ONS variable catalog — no network. */
export const UK_POSTCODE_CATALOG: DemographicVariable[] = buildCatalog(UK_SEEDS, {
  country: "UK",
  citation: UK_POSTCODE_CITATION,
  tiers: UK_POSTCODE_TIERS,
});

export function createUkPostcodeAdapter(): OverlayAdapter {
  return {
    source: UK_POSTCODE_SOURCE,
    country: "UK",
    tiers: UK_POSTCODE_TIERS,

    catalog() {
      return UK_POSTCODE_CATALOG;
    },

    async fetchRawBoundaries(http: HttpClient, tier: Tier, vintage) {
      return http.getJson<FeatureCollection>(`${ONS_API_BASE}/geography/${vintage}/${tier}.geojson`);
    },

    normalizeBoundaries(raw, tier, vintage): Boundary[] {
      return normalizeFeatureCollection(raw, {
        country: "UK",
        tier,
        vintage,
        // ONS codes/names vary by tier column name (e.g. RGN21CD/RGN21NM, LAD21CD/LAD21NM).
        idFrom: (props) => {
          const codeKey = Object.keys(props).find((k) => /CD$/i.test(k));
          return String((codeKey ? props[codeKey] : undefined) ?? props.geography_code ?? props.code ?? "");
        },
        nameFrom: (props) => {
          const nameKey = Object.keys(props).find((k) => /NM$/i.test(k));
          return (nameKey ? props[nameKey] : undefined) ?? props.name;
        },
      });
    },

    async fetchRawValues(http: HttpClient, variableId: string, _tier: Tier, vintage) {
      return http.getText(`${ONS_API_BASE}/dataset/${vintage}/${variableId}.data.csv`);
    },

    normalizeValues(raw, variableId, _tier, vintage): DemographicDatum[] {
      const text = String(raw ?? "");
      const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
      if (lines.length < 2) return [];
      const header = lines[0]!.split(",").map((h) => h.trim().toLowerCase());
      const codeIdx = header.findIndex((h) => h === "geography_code" || h === "geography code" || h === "code");
      const valIdx = header.findIndex((h) => h === "value" || h === "obs_value" || h === "count");
      if (codeIdx === -1 || valIdx === -1) return [];
      const out: DemographicDatum[] = [];
      for (let i = 1; i < lines.length; i++) {
        const cells = lines[i]!.split(",");
        const id = (cells[codeIdx] ?? "").trim();
        if (id === "") continue;
        const valStr = cells[valIdx];
        const num = valStr == null || valStr.trim() === "" ? null : Number(valStr);
        out.push({
          boundaryId: id,
          variableId,
          vintage,
          value: num !== null && Number.isFinite(num) ? num : null,
        });
      }
      return out;
    },
  };
}
